/**
 * useWorkspaceMembers Hook
 *
 * Fetches workspace members and subscribes to changes.
 * Uses cache so Members page shows data instantly when preloaded (e.g. from SettingsDataPreloader).
 */

import { useEffect, useState, useRef } from "react";
import type React from "react";
import { auth, db } from "../../../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  Timestamp,
} from "firebase/firestore";
import { Workspace, WorkspaceMemberWithDetails, WorkspaceRole } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";
import { validateUserDocumentExists } from "../../../utils/memberUtils";

const membersCache = new Map<string, { members: WorkspaceMemberWithDetails[]; timestamp: number }>();
const CACHE_TTL_MS = 2 * 60 * 1000; // 2 min

export interface UseWorkspaceMembersParams {
  workspaceId: string;
  workspace: Workspace | null;
}

export interface UseWorkspaceMembersResult {
  members: WorkspaceMemberWithDetails[];
  currentUserRole: WorkspaceRole;
  setMembers: React.Dispatch<React.SetStateAction<WorkspaceMemberWithDetails[]>>;
  setCurrentUserRole: React.Dispatch<React.SetStateAction<WorkspaceRole>>;
  refresh: () => Promise<void>;
}

function getCurrentUserRoleFromMembers(members: WorkspaceMemberWithDetails[]): WorkspaceRole {
  const currentUser = auth.currentUser;
  if (!currentUser) return "user";
  const member = members.find((m) => m.id === currentUser.uid);
  if (!member) return "user";
  if (member.role === "owner") return "owner";
  if (member.role === "admin") return "admin";
  return "user";
}

/**
 * Hook for fetching workspace members
 */
export const useWorkspaceMembers = ({
  workspaceId,
  workspace,
}: UseWorkspaceMembersParams): UseWorkspaceMembersResult => {
  const cached = workspaceId ? membersCache.get(workspaceId) : null;
  const hasValidCache = !!(cached && (Date.now() - cached.timestamp) < CACHE_TTL_MS);
  const initialMembers = hasValidCache && cached ? cached.members : [];

  const [members, setMembers] = useState<WorkspaceMemberWithDetails[]>(initialMembers);
  const [currentUserRole, setCurrentUserRole] = useState<WorkspaceRole>(
    () => (hasValidCache && cached ? getCurrentUserRoleFromMembers(cached.members) : "user")
  );
  // Use ref to track latest members for use in callbacks (avoid closure issues)
  const membersRef = useRef<WorkspaceMemberWithDetails[]>([]);

  // Keep ref in sync with state
  useEffect(() => {
    membersRef.current = members;
  }, [members]);

  // Uses membersRef to get latest members data to avoid closure issues
  const fetchMembers = async (workspaceData: Workspace | null): Promise<void> => {
    try {
      if (!workspaceData) return;

      // Preserve existing members data to avoid losing fetched user details
      const existingMembers = membersRef.current.length > 0 ? membersRef.current : [];
      const existingMembersMap = new Map(existingMembers.map((m: WorkspaceMemberWithDetails) => [m.id, m]));

      const membersCollectionRef = collection(db, "workspaces", workspaceId, "members");
      const membersSnapshot = await getDocs(membersCollectionRef);

      const membersList: WorkspaceMemberWithDetails[] = await Promise.all(
        membersSnapshot.docs.map(async (memberDoc) => {
          const memberData = memberDoc.data();
          const userId = memberDoc.id;

          // Get existing member data if available (to preserve already-fetched user details)
          const existingMember = existingMembersMap.get(userId);

          // Get email and displayName from member document as fallback
          const memberEmail = (memberData.email as string | undefined) || undefined;
          const memberDisplayName = (memberData.displayName as string | undefined) || undefined;

          // Fetch user details (may fail in demo mode due to permissions)
          let name: string | undefined;
          let email: string | undefined;
          let photoURL: string | undefined;
          let fetchSucceeded = false;

          try {
            const userDoc = await getDoc(doc(db, "users", userId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              // Check if user document has meaningful data
              const hasUserData = !!(userData.name || userData.email || userData.displayName);
              
              if (hasUserData) {
                name = (userData.name as string | undefined) || (userData.displayName as string | undefined);
                email = userData.email as string | undefined;
                photoURL = userData.photoURL as string | undefined;
                fetchSucceeded = true;
              } else {
                // User document exists but has no data - this is an orphaned member
                // Log warning but continue (cleanup utility will handle removal)
                console.warn(`⚠️ Orphaned member detected: ${userId} - user document exists but has no data`);
              }
            } else {
              // User document doesn't exist - this is an orphaned member
              // Log warning but continue (cleanup utility will handle removal)
              console.warn(`⚠️ Orphaned member detected: ${userId} - user document doesn't exist`);
            }
          } catch (error) {
            // In demo mode, user documents may not be accessible - this is OK
            // We'll use member document data as fallback
          }

          // Ensure owner role is set correctly, and map legacy "member" role to "user"
          let role: "owner" | "member" | "admin" | "user" | "viewer";
          if (workspaceData.ownerId === userId) {
            role = "owner";
          } else {
            const memberRole = memberData.role as string | undefined;
            // Map legacy "member" role to "user"
            if (!memberRole || memberRole === "member") {
              role = "user";
            } else if (["admin", "user", "viewer", "owner"].includes(memberRole)) {
              role = memberRole as "owner" | "member" | "admin" | "user" | "viewer";
            } else {
              role = "user"; // Default to user for unknown roles
            }
          }

          // Merge fetched data with existing data and member document data
          // Priority: newly fetched user data > existing member data > member document data > undefined
          return {
            id: userId,
            role,
            joinedAt: memberData.joinedAt,
            // Use newly fetched name if available, otherwise existing, otherwise member displayName
            name: fetchSucceeded && name 
              ? name 
              : (existingMember?.name || name || memberDisplayName),
            // Use newly fetched email if available, otherwise existing, otherwise member email
            email: fetchSucceeded && email 
              ? email 
              : (existingMember?.email || email || memberEmail),
            // Use newly fetched photoURL if available, otherwise existing
            photoURL: fetchSucceeded && photoURL 
              ? photoURL 
              : (existingMember?.photoURL || photoURL),
            status: "active" as const,
          };
        })
      );

      // Always fetch owner's user details separately to ensure we have the latest info
      // This is important because owner might be in members list but without complete details
      let ownerName: string | undefined;
      let ownerEmail: string | undefined;
      let ownerPhotoURL: string | undefined;
      let ownerFetchSucceeded = false;
      
      // Get owner's member document data as fallback
      let ownerMemberEmail: string | undefined;
      let ownerMemberDisplayName: string | undefined;
      try {
        const ownerMemberDoc = await getDoc(doc(db, "workspaces", workspaceId, "members", workspaceData.ownerId));
        if (ownerMemberDoc.exists()) {
          const ownerMemberData = ownerMemberDoc.data();
          ownerMemberEmail = ownerMemberData.email as string | undefined;
          ownerMemberDisplayName = ownerMemberData.displayName as string | undefined;
        }
      } catch (error) {
        // Member document might not exist or not accessible - that's OK
      }

      try {
        const ownerUserDoc = await getDoc(doc(db, "users", workspaceData.ownerId));
        if (ownerUserDoc.exists()) {
          const ownerUserData = ownerUserDoc.data();
          ownerName = (ownerUserData.name as string | undefined) || (ownerUserData.displayName as string | undefined);
          ownerEmail = ownerUserData.email as string | undefined;
          ownerPhotoURL = ownerUserData.photoURL as string | undefined;
          ownerFetchSucceeded = true;
        }
      } catch (error) {
        // In demo mode, user documents may not be accessible - this is OK
        // We'll use member document data as fallback
      }

      // Ensure workspace owner is always in the members list
      const ownerIndex = membersList.findIndex((m) => m.id === workspaceData.ownerId);
      
      if (ownerIndex === -1) {
        // Owner not in list - add them at the beginning
        // Use member document data as fallback if user document fetch failed
        const ownerMember: WorkspaceMemberWithDetails = {
          id: workspaceData.ownerId,
          role: "owner",
          joinedAt: workspaceData.createdAt,
          status: "active",
          name: ownerName || ownerMemberDisplayName,
          email: ownerEmail || ownerMemberEmail,
          photoURL: ownerPhotoURL,
        };
        membersList.unshift(ownerMember);
      } else {
        // Owner is already in list - merge with existing data (don't overwrite existing good data)
        const existingOwner = membersList[ownerIndex];
        
        // Priority: newly fetched user data > existing data > member document data > undefined
        const updatedOwner: WorkspaceMemberWithDetails = {
          ...existingOwner, // Start with all existing data
          role: "owner", // Ensure role is correct
          // Use fetched data if available, otherwise keep existing, otherwise use member document data
          name: ownerFetchSucceeded && ownerName 
            ? ownerName 
            : (existingOwner.name || ownerName || ownerMemberDisplayName),
          email: ownerFetchSucceeded && ownerEmail 
            ? ownerEmail 
            : (existingOwner.email || ownerEmail || ownerMemberEmail),
          photoURL: ownerFetchSucceeded && ownerPhotoURL 
            ? ownerPhotoURL 
            : (existingOwner.photoURL || ownerPhotoURL),
        };
        
        membersList[ownerIndex] = updatedOwner;
        
        // Move to front if not already there
        if (ownerIndex > 0) {
          const movedOwner = membersList.splice(ownerIndex, 1)[0];
          membersList.unshift(movedOwner);
        }
      }

      // Sort members: owner first, then others by name/email
      membersList.sort((a, b) => {
        // Owner always first
        if (a.id === workspaceData.ownerId) return -1;
        if (b.id === workspaceData.ownerId) return 1;
        // Then sort by name or email
        const aName = a.name || a.email || "";
        const bName = b.name || b.email || "";
        return aName.localeCompare(bName);
      });

      // Create a new array reference to ensure React detects the change
      const list = [...membersList];
      setMembers(list);
      if (workspaceId) {
        membersCache.set(workspaceId, { members: list, timestamp: Date.now() });
      }

      // Set current user's role
      const currentUser = auth.currentUser;
      if (currentUser) {
        const currentMember = membersList.find((m) => m.id === currentUser.uid);
        if (currentMember) {
          // Map member role to WorkspaceRole
          if (currentMember.role === "owner") {
            setCurrentUserRole("owner");
          } else if (currentMember.role === "admin") {
            setCurrentUserRole("admin");
          } else {
            setCurrentUserRole("user");
          }
        }
      }
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  useEffect(() => {
    if (!workspace) return;

    // Initial fetch
    fetchMembers(workspace);

    // Subscribe to members changes
    const unsubscribeMembers = onSnapshot(
      collection(db, "workspaces", workspaceId, "members"),
      async () => {
        // Get current workspace data to avoid stale closure
        const currentWorkspaceDoc = await getDoc(doc(db, "workspaces", workspaceId));
        if (currentWorkspaceDoc.exists()) {
          const data = currentWorkspaceDoc.data();
          const currentWorkspace: Workspace = {
            id: currentWorkspaceDoc.id,
            name: data.name as string,
            ownerId: data.ownerId as string,
            logoUrl: data.logoUrl as string | undefined,
            createdAt: data.createdAt,
          };
          // Refetch members, preserving current member data via ref
          await fetchMembers(currentWorkspace);
        }
      }
    );

    return () => {
      unsubscribeMembers();
    };
  }, [workspaceId, workspace]);

  return {
    members,
    currentUserRole,
    setMembers,
    setCurrentUserRole,
    refresh: () => fetchMembers(workspace),
  };
};
